// apps/account-portal/src/services/api.ts
import axios from 'axios';
import { Role } from '@redpanda/types';
import { getCookie } from '@redpanda/utils';
import { getVisitorId } from '../utils/visitorId';

const createApiClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    withCredentials: true, // 包含 cookies 進行認證
  });

  // 請求攔截器：優先使用 Authorization header，降級到 cookies
  client.interceptors.request.use(
    (config) => {
      // 1. 嘗試從 Cookie 中取得 token
      const token = getCookie('auth_token');

      if (token) {
        // 跨域請求使用 Authorization header
        config.headers.Authorization = `Bearer ${token}`;
      }

      // 同時保留 credentials 設定作為降級方案
      config.withCredentials = true;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return client;
};


const identityApi = createApiClient(process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api');
const subscriptionApi = createApiClient(process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api');

// 留言、許願：沒登入時用訪客 id 區分誰按過讚／投過票（登入後後端以帳號為準）
subscriptionApi.interceptors.request.use((config) => {
  if (/^\/(board|wishlist)/.test(config.url || '')) {
    const visitorId = getVisitorId();
    if (visitorId) config.headers['X-Visitor-Id'] = visitorId;
  }
  return config;
});

// 用戶管理 API
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');
export const getPendingUsers = () => identityApi.get('/admin/users/pending');
export const updateUserRole = (userId: string, role: Role) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });
// 核准／拒絕用戶：與 CRM 相同走 /approve、/reject（/status 不會設定 allowedSystems）
export const approveUser = (userId: string, role: Role, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);

// 留言板 API
export const boardApi = {
  getPosts: (params?: { page?: number; pageSize?: number; type?: string; q?: string }) =>
    subscriptionApi.get('/board', { params }),
  createPost: (data: { postType: string; title: string; content: string }) =>
    subscriptionApi.post('/board', data),
  updatePost: (id: string, data: { postType: string; title: string; content: string }) =>
    subscriptionApi.patch(`/board/${id}`, data),
  deletePost: (id: string) => subscriptionApi.delete(`/board/${id}`),
  likePost: (id: string) => subscriptionApi.post(`/board/${id}/like`),
  unlikePost: (id: string) => subscriptionApi.delete(`/board/${id}/like`),
  getComments: (id: string) => subscriptionApi.get(`/board/${id}/comments`),
  createComment: (id: string, data: { content: string }) =>
    subscriptionApi.post(`/board/${id}/comments`, data),
  updateComment: (postId: string, commentId: string, data: { content: string }) =>
    subscriptionApi.patch(`/board/${postId}/comments/${commentId}`, data),
  deleteComment: (postId: string, commentId: string) =>
    subscriptionApi.delete(`/board/${postId}/comments/${commentId}`),
};

// 許願清單 API
export const wishlistApi = {
  getPosts: (params?: { page?: number; pageSize?: number; status?: string; q?: string; sort?: 'top' | 'new' }) =>
    subscriptionApi.get('/wishlist', { params }),
  createPost: (data: { title: string; content: string }) =>
    subscriptionApi.post('/wishlist', data),
  updatePost: (id: string, data: { title: string; content: string }) =>
    subscriptionApi.patch(`/wishlist/${id}`, data),
  deletePost: (id: string) => subscriptionApi.delete(`/wishlist/${id}`),
  votePost: (id: string) => subscriptionApi.post(`/wishlist/${id}/vote`),
  unvotePost: (id: string) => subscriptionApi.delete(`/wishlist/${id}/vote`),
};

export { identityApi, subscriptionApi };